import React, { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { createPortal } from "react-dom";
import { ChevronDown, Search, X } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-hot-toast";

/* eslint-disable import/order */
import ClubbieModal from "../Modal/ClubbieModal";
import { addFixtureResult } from "../../redux/slices/tournamentSlice";
import TeamBadge from "../TeamBadge/TeamBadge";
import TeamToggle from "./TeamToggle";
import { fixedValues } from "../../utils/fixedValues";
import { resultRecorded } from "../../utils/analytics";
import { ReactComponent as AddIcon } from "../../assets/images/icons/add.svg";
import { ReactComponent as GoalsScoredIcon } from "../../assets/images/icons/goals-scored.svg";
import { ReactComponent as YellowIcon } from "../../assets/images/icons/yellow-card.svg";
import { ReactComponent as RedIcon } from "../../assets/images/icons/red-card.svg";
import { ReactComponent as SubIcon } from "../../assets/images/icons/sub.svg";
import { ReactComponent as SubOffIcon } from "../../assets/images/icons/sub-off.svg";
import { ReactComponent as SubOnIcon } from "../../assets/images/icons/sub-on.svg";
import { ReactComponent as PenaltyIcon } from "../../assets/images/icons/penalties.svg";
/* eslint-enable import/order */

const MDT = fixedValues.matchDetailTypes;
const SPORTS = fixedValues.sportTypes;

// Per-sport label maps for the event picker + the loaded-events filter.
// Cards / SECOND_YELLOW / SUBSTITUTIONS are universal across sports.
const SPORT_EVENT_LABELS = {
  [SPORTS.FOOTBALL]: {
    [MDT.GOALS]: "Goal",
    [MDT.PENALTY_GOALS]: "Penalty",
    [MDT.OWN_GOALS]: "Own Goal",
    [MDT.YELLOW_CARDS]: "Yellow Card",
    [MDT.SECOND_YELLOW_CARDS]: "2nd Yellow",
    [MDT.RED_CARDS]: "Red Card",
    [MDT.SUBSTITUTIONS]: "Substitution",
  },
  [SPORTS.RUGBY]: {
    [MDT.TRY]: "Try",
    [MDT.CONVERSION]: "Conversion",
    [MDT.PENALTY]: "Penalty",
    [MDT.DROP_GOAL]: "Drop Goal",
    [MDT.YELLOW_CARDS]: "Yellow Card",
    [MDT.SECOND_YELLOW_CARDS]: "2nd Yellow",
    [MDT.RED_CARDS]: "Red Card",
    [MDT.SUBSTITUTIONS]: "Substitution",
  },
  [SPORTS.TOUCH_RUGBY]: {
    [MDT.TRY]: "Try",
    [MDT.PENALTY]: "Penalty",
    [MDT.YELLOW_CARDS]: "Yellow Card",
    [MDT.SECOND_YELLOW_CARDS]: "2nd Yellow",
    [MDT.RED_CARDS]: "Red Card",
    [MDT.SUBSTITUTIONS]: "Substitution",
  },
};

// Per-sport scoring map: matchDetailType → point value for the auto-sync.
// (Manual home/away score inputs remain the source of truth on save.)
const SPORT_SCORE_POINTS = {
  [SPORTS.FOOTBALL]: { [MDT.GOALS]: 1, [MDT.PENALTY_GOALS]: 1, [MDT.OWN_GOALS]: 1 },
  [SPORTS.RUGBY]: { [MDT.TRY]: 5, [MDT.CONVERSION]: 2, [MDT.PENALTY]: 3, [MDT.DROP_GOAL]: 3 },
  [SPORTS.TOUCH_RUGBY]: { [MDT.TRY]: 1, [MDT.PENALTY]: 1 },
};

// Event types the picker offers, ordered as on mobile.
const SPORT_PICKER_TYPES = {
  [SPORTS.FOOTBALL]: [MDT.GOALS, MDT.PENALTY_GOALS, MDT.OWN_GOALS, MDT.YELLOW_CARDS, MDT.RED_CARDS, MDT.SUBSTITUTIONS],
  [SPORTS.RUGBY]: [MDT.TRY, MDT.CONVERSION, MDT.PENALTY, MDT.DROP_GOAL, MDT.YELLOW_CARDS, MDT.RED_CARDS, MDT.SUBSTITUTIONS],
  [SPORTS.TOUCH_RUGBY]: [MDT.TRY, MDT.PENALTY, MDT.YELLOW_CARDS, MDT.RED_CARDS, MDT.SUBSTITUTIONS],
};

// Resolve the actual team entity ID from fixture team fields
const resolveActualId = (raw) =>
  raw?._id || raw?.id || (typeof raw === "string" ? raw : "") || "";

/**
 * AddResultModal - Modal for adding/editing match results
 * Matches Figma design with event types: Goal, Penalty, Yellow Card, Red Card, Substitution
 */
const AddResultModal = ({ isOpen, onClose, result, fixture, type = "add" }) => {
  const [events, setEvents] = useState([]);
  const [openEventMenu, setOpenEventMenu] = useState(false);
  const [activeEventType, setActiveEventType] = useState(null);
  const [isTeam1Selected, setIsTeam1Selected] = useState(false);
  const [eventData, setEventData] = useState({
    mainPlayer: null,
    assistPlayer: null,
    leavingPlayer: null,
    // Rugby TRY only — toggle + scorer for the optional conversion.
    tryConverted: false,
    conversionPlayer: null,
  });

  // Direct score inputs (override event-based scoring)
  const [homeScore, setHomeScore] = useState(0);
  const [awayScore, setAwayScore] = useState(0);
  const [useDirectScores] = useState(true);

  const dropdownRef = useRef(null);
  const addEventRef = useRef(null);
  const [eventMenuPos, setEventMenuPos] = useState({ top: 0, bottom: 0, left: 0, width: 0, dropUp: false });
  const userMadeChangesRef = useRef(false);

  const dispatch = useDispatch();
  const { selectedTournament, selectedCategory } = useSelector(
    (state) => state.tournament
  );

  // Sport drives picker / scoring / form branches. Default to football for safety.
  const sportType = selectedTournament?.sport || SPORTS.FOOTBALL;
  const eventLabels = SPORT_EVENT_LABELS[sportType] || SPORT_EVENT_LABELS[SPORTS.FOOTBALL];
  const scorePoints = SPORT_SCORE_POINTS[sportType] || SPORT_SCORE_POINTS[SPORTS.FOOTBALL];
  const pickerTypes = SPORT_PICKER_TYPES[sportType] || SPORT_PICKER_TYPES[SPORTS.FOOTBALL];

  // Initialize events and scores from fixture when modal opens
  useEffect(() => {
    if (isOpen && fixture) {
      userMadeChangesRef.current = false;
      // Restore saved scores; auto-score effect is guarded and won't override these on init
      setHomeScore(fixture.homeTeamScore ?? 0);
      setAwayScore(fixture.awayTeamScore ?? 0);

      // Load existing matchDetails as events
      if (fixture.matchDetails && Array.isArray(fixture.matchDetails)) {
        const homeTeamTitle = fixture?.homeTeam?.title || fixture?.homeTeam?.name || "Team 1";
        const awayTeamTitle = fixture?.awayTeam?.title || fixture?.awayTeam?.name || "Team 2";
        const homeId = resolveActualId(fixture.homeTeamActualId);

        const loadedEvents = fixture.matchDetails
          .filter((d) => d.matchDetailType && eventLabels[d.matchDetailType])
          .map((detail) => {
            const meta = detail.metadata || {};
            const teamName = detail.team && String(detail.team) === String(homeId)
              ? homeTeamTitle
              : awayTeamTitle;
            return {
              matchDetailType: detail.matchDetailType,
              rosterPlayerId: detail.rosterPlayerId || '',
              playerName: detail.playerName || '',
              assistRosterPlayerId: meta.assist?.rosterPlayerId || '',
              assistPlayerName: meta.assist?.playerName || '',
              leavingRosterPlayerId: meta.leavingPlayer?.rosterPlayerId || '',
              leavingPlayerName: meta.leavingPlayer?.playerName || '',
              teamName,
              teamId: detail.team || '',
              isExisting: true,
              uid: detail.uid || '',
            };
          });

        setEvents(loadedEvents);
      } else {
        setEvents([]);
      }

    }
  }, [isOpen, fixture]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target) &&
        addEventRef.current &&
        !addEventRef.current.contains(e.target)
      ) {
        setOpenEventMenu(false);
      }
    };

    if (openEventMenu) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [openEventMenu]);

  // Get team data from fixture
  const homeTeam = {
    title: fixture?.homeTeam?.title || fixture?.homeTeam?.name || "Team 1",
    crest: fixture?.homeTeam?.crest || fixture?.homeTeam?.logo,
  };

  const awayTeam = {
    title: fixture?.awayTeam?.title || fixture?.awayTeam?.name || "Team 2",
    crest: fixture?.awayTeam?.crest || fixture?.awayTeam?.logo,
  };

  // Compute cumulative score at each event for per-event display.
  // Football keeps the OWN_GOALS-credits-opponent quirk; rugby/touch don't have
  // that concept and use the sport's flat point map.
  const eventsWithScores = useMemo(() => {
    let home = 0;
    let away = 0;
    return events.map((event) => {
      const points = scorePoints[event.matchDetailType];
      if (points && event.teamName) {
        if (event.matchDetailType === MDT.OWN_GOALS) {
          if (event.teamName === homeTeam.title) away += points;
          else if (event.teamName === awayTeam.title) home += points;
        } else {
          if (event.teamName === homeTeam.title) home += points;
          else if (event.teamName === awayTeam.title) away += points;
        }
      }
      return { ...event, scoreAtEvent: `${home}:${away}` };
    });
  }, [events, homeTeam.title, awayTeam.title, scorePoints]);

  // Auto-sync score inputs when user adds or removes events (not on initial load)
  useEffect(() => {
    if (!userMadeChangesRef.current) return;
    let home = 0;
    let away = 0;
    events.forEach((e) => {
      const points = scorePoints[e.matchDetailType];
      if (!points || !e.teamName) return;
      if (e.matchDetailType === MDT.OWN_GOALS) {
        if (e.teamName === homeTeam.title) away += points;
        else if (e.teamName === awayTeam.title) home += points;
      } else {
        if (e.teamName === homeTeam.title) home += points;
        else if (e.teamName === awayTeam.title) away += points;
      }
    });
    setHomeScore(home);
    setAwayScore(away);
  }, [events, homeTeam.title, awayTeam.title, scorePoints]);

  const handleAddEvent = (matchDetailType) => {
    setActiveEventType(matchDetailType);
    setOpenEventMenu(false);
    setEventData({ mainPlayer: null, assistPlayer: null, leavingPlayer: null });
  };

  const handleConfirmEvent = () => {
    const selectedTeamName = isTeam1Selected ? awayTeam.title : homeTeam.title;
    const selectedTeamId = isTeam1Selected
      ? resolveActualId(fixture?.awayTeamActualId)
      : resolveActualId(fixture?.homeTeamActualId);

    // Auto-detect second yellow: if this player already has a yellow card for this team
    let resolvedType = activeEventType;
    if (activeEventType === MDT.YELLOW_CARDS && eventData.mainPlayer) {
      const alreadyHasYellow = events.some(
        (e) =>
          e.matchDetailType === MDT.YELLOW_CARDS &&
          e.teamName === selectedTeamName &&
          e.playerName === eventData.mainPlayer
      );
      if (alreadyHasYellow) resolvedType = MDT.SECOND_YELLOW_CARDS;
    }

    const lookupName = (rosterPlayerId) =>
      currentTeamPlayers.find((p) => p.value === rosterPlayerId)?.label || '';

    const baseUid = String(Date.now());
    const newEvent = {
      matchDetailType: resolvedType,
      rosterPlayerId: eventData.mainPlayer || '',
      playerName: lookupName(eventData.mainPlayer),
      assistRosterPlayerId: eventData.assistPlayer || '',
      assistPlayerName: lookupName(eventData.assistPlayer),
      leavingRosterPlayerId: eventData.leavingPlayer || '',
      leavingPlayerName: lookupName(eventData.leavingPlayer),
      teamName: selectedTeamName,
      teamId: selectedTeamId,
      isExisting: false,
      uid: baseUid,
    };

    // Rugby-only: when TRY is added with the "Did it convert?" toggle on,
    // push a *separate* CONVERSION row immediately. Matches mobile (which
    // emits two distinct match details) and makes the conversion visible to
    // the admin right away, even if the squad isn't loaded and no player got
    // picked. The CONVERSION row carries whatever player info we have (or
    // empty fields if none).
    const includeConversionRow =
      sportType === SPORTS.RUGBY && resolvedType === MDT.TRY && eventData.tryConverted;

    const conversionRow = includeConversionRow
      ? {
          matchDetailType: MDT.CONVERSION,
          rosterPlayerId: eventData.conversionPlayer || '',
          playerName: lookupName(eventData.conversionPlayer),
          assistRosterPlayerId: '',
          assistPlayerName: '',
          leavingRosterPlayerId: '',
          leavingPlayerName: '',
          teamName: selectedTeamName,
          teamId: selectedTeamId,
          isExisting: false,
          uid: `${baseUid}-conv`,
        }
      : null;

    userMadeChangesRef.current = true;
    setEvents((prev) => (conversionRow ? [...prev, newEvent, conversionRow] : [...prev, newEvent]));
    setActiveEventType(null);
    setEventData({
      mainPlayer: null,
      assistPlayer: null,
      leavingPlayer: null,
      tryConverted: false,
      conversionPlayer: null,
    });
    toast.success(`${eventLabels[resolvedType] || resolvedType} added`);
  };

  const handleCancelEvent = () => {
    setActiveEventType(null);
    setEventData({
      mainPlayer: null,
      assistPlayer: null,
      leavingPlayer: null,
      tryConverted: false,
      conversionPlayer: null,
    });
  };

  // Extract roster players for a given team ID from selectedCategory
  const getTeamPlayers = useCallback(
    (teamId) => {
      try {
        const team = selectedCategory?.teams.find((t) => {
          const tId = t.teamId?._id || t.teamId?.id || t._id || t.id;
          return tId === teamId;
        });
        if (!team?.rosterPlayers?.length) return [];
        return team.rosterPlayers.map((player) => ({
          value: player._id,           // rosterPlayer _id
          label: player.name || "Unknown",
        }));
      } catch {
        return [];
      }
    },
    [selectedCategory?.teams]
  );

  // Resolve a rosterPlayer's underlying userId so match details can carry a
  // real `player` ref. Personal-stats queries match on matchDetail.player; if
  // we leave it null the goal/assist won't count for that user.
  const getRosterPlayerUserId = useCallback(
    (rosterPlayerId, teamId) => {
      if (!rosterPlayerId || !teamId) return null;
      const team = selectedCategory?.teams?.find((t) => {
        const tId = t.teamId?._id || t.teamId?.id || t._id || t.id;
        return tId?.toString() === teamId.toString();
      });
      const rp = team?.rosterPlayers?.find(
        (p) => p._id?.toString() === rosterPlayerId.toString()
      );
      return rp?.userId || null;
    },
    [selectedCategory?.teams]
  );

  // Current team players based on toggle (false = home, true = away)
  const currentTeamId = isTeam1Selected
    ? resolveActualId(fixture?.awayTeamActualId)
    : resolveActualId(fixture?.homeTeamActualId);
  const currentTeamPlayers = useMemo(
    () => getTeamPlayers(currentTeamId),
    [currentTeamId, getTeamPlayers]
  );

  // Reset player selections when switching teams
  useEffect(() => {
    setEventData({
      mainPlayer: null,
      assistPlayer: null,
      leavingPlayer: null,
      tryConverted: false,
      conversionPlayer: null,
    });
  }, [isTeam1Selected]);

  const handleSaveResults = async () => {
    if (!fixture || !selectedTournament) {
      toast.error("Missing fixture or tournament data");
      return;
    }

    const loadingToast = toast.loading("Saving results...");

    try {
      // Use directly entered scores
      const homeTeamScore = typeof homeScore === "number" ? homeScore : parseInt(homeScore) || 0;
      const awayTeamScore = typeof awayScore === "number" ? awayScore : parseInt(awayScore) || 0;

      // Build match details — 1:1 map of local events. Rugby TRY+conversion
      // is already represented as two separate event rows in `events` (added
      // at handleConfirmEvent time), so no fan-out is needed here.
      // Note: rosterPlayerId is an ObjectId in the schema, so we omit the
      // field entirely when blank — sending "" causes a cast failure.
      const matchDetails = events.map((event, i) => ({
        index: i,
        matchDetailType: event.matchDetailType,
        ...(event.rosterPlayerId && { rosterPlayerId: event.rosterPlayerId }),
        player: getRosterPlayerUserId(event.rosterPlayerId, event.teamId),
        playerName: event.playerName || '',
        team: event.teamId || '',
        metadata: {
          ...(event.assistPlayerName && {
            assist: {
              ...(event.assistRosterPlayerId && { rosterPlayerId: event.assistRosterPlayerId }),
              player: getRosterPlayerUserId(event.assistRosterPlayerId, event.teamId),
              playerName: event.assistPlayerName,
            },
          }),
          ...(event.leavingPlayerName && {
            leavingPlayer: {
              ...(event.leavingRosterPlayerId && { rosterPlayerId: event.leavingRosterPlayerId }),
              player: getRosterPlayerUserId(event.leavingRosterPlayerId, event.teamId),
              playerName: event.leavingPlayerName,
            },
          }),
        },
        uid: event.uid || String(Date.now() + i),
      }));

      const resultData = {
        fixture: {
          _id: fixture._id,
          tournamentId:
            fixture.tournamentId || selectedTournament?.id || selectedTournament?._id,
          categoryId: fixture.categoryId || selectedCategory?._id || selectedCategory?.id,
          phaseId: fixture.phaseId,
          blockId: fixture.blockId,
          homeTeamActualId: fixture.homeTeamActualId?._id || fixture.homeTeamActualId?.id || fixture.homeTeamActualId,
          awayTeamActualId: fixture.awayTeamActualId?._id || fixture.awayTeamActualId?.id || fixture.awayTeamActualId,
          // Only include eventId if it exists (not all fixtures have events)
          ...(fixture.eventId && { eventId: fixture.eventId?._id || fixture.eventId?.id || fixture.eventId }),
          ...(fixture.eventAwayId && { eventAwayId: fixture.eventAwayId?._id || fixture.eventAwayId?.id || fixture.eventAwayId }),
        },
        teamScores: {
          homeTeamScore,
          awayTeamScore,
        },
        matchDetails,
        playerStats: [],
      };

      await dispatch(
        addFixtureResult({
          tournamentId: selectedTournament.id || selectedTournament._id,
          resultData,
        })
      ).unwrap();

      resultRecorded({
        competitionId: selectedTournament.id || selectedTournament._id,
        fixtureId: fixture._id,
        homeScore: homeTeamScore,
        awayScore: awayTeamScore,
      });

      toast.dismiss(loadingToast);
      toast.success("Results saved successfully!");
      onClose();
    } catch (error) {
      toast.dismiss(loadingToast);
      toast.error(error.message || "Failed to save results");
    }
  };

  const getEventIcon = (matchDetailType) => {
    switch (matchDetailType) {
      case MDT.GOALS:
      case MDT.OWN_GOALS:
      case MDT.TRY:
      case MDT.DROP_GOAL:
        return <GoalsScoredIcon className="w-6 h-6" />;
      case MDT.PENALTY_GOALS:
      case MDT.PENALTY:
      case MDT.CONVERSION:
        return <PenaltyIcon className="w-6 h-6" />;
      case MDT.YELLOW_CARDS:
      case MDT.SECOND_YELLOW_CARDS:
        return <YellowIcon className="w-6 h-6" />;
      case MDT.RED_CARDS:
        return <RedIcon className="w-6 h-6" />;
      case MDT.SUBSTITUTIONS:
        return <SubIcon className="w-6 h-6" />;
      default:
        return null;
    }
  };

  const renderEventForm = () => {
    if (!activeEventType) return null;

    // Football
    const isGoal = activeEventType === MDT.GOALS;
    const isPenaltyGoal = activeEventType === MDT.PENALTY_GOALS;
    const isOwnGoal = activeEventType === MDT.OWN_GOALS;
    // Rugby / touch rugby
    const isTry = activeEventType === MDT.TRY;
    const isConversion = activeEventType === MDT.CONVERSION;
    const isPenalty = activeEventType === MDT.PENALTY;
    const isDropGoal = activeEventType === MDT.DROP_GOAL;
    // Universal
    const isCard = activeEventType === MDT.YELLOW_CARDS || activeEventType === MDT.RED_CARDS;
    const isSub = activeEventType === MDT.SUBSTITUTIONS;

    const label = eventLabels[activeEventType] || activeEventType;
    const showAssist = isGoal; // football-only — mobile rugby/touch don't track assist
    const showTryConversionToggle = sportType === SPORTS.RUGBY && isTry;

    let mainLabel = "Select player";
    if (isGoal || isPenaltyGoal || isTry || isConversion || isPenalty || isDropGoal) mainLabel = "Who scored?";
    else if (isOwnGoal) mainLabel = "Who scored the own goal?";
    else if (isCard) mainLabel = `Who received the ${label.toLowerCase()}?`;
    else if (isSub) mainLabel = "Who is coming on?";

    return (
      <div className="mt-4 p-4 border border-[#192634] rounded-2xl space-y-4">
        <h4 className="text-lg font-semibold text-[#FEFCEB]">
          Add {label.toLowerCase()}
        </h4>

        {/* Team Toggle */}
        <TeamToggle
          team1={homeTeam.title}
          team2={awayTeam.title}
          isTeam1Selected={isTeam1Selected}
          onToggle={setIsTeam1Selected}
        />

        {/* Main Player Input */}
        <div>
          <label className="block text-sm text-[#FEFCEB] mb-2">{mainLabel}</label>
          <PlayerDropdown
            players={currentTeamPlayers}
            value={eventData.mainPlayer}
            onChange={(val) => setEventData((prev) => ({ ...prev, mainPlayer: val }))}
            placeholder="Select player"
          />
        </div>

        {/* Assist Player (Football goals only) */}
        {showAssist && (
          <div>
            <label className="block text-sm text-[#FEFCEB] mb-2">Who got the assist?</label>
            <PlayerDropdown
              players={currentTeamPlayers}
              value={eventData.assistPlayer}
              onChange={(val) => setEventData((prev) => ({ ...prev, assistPlayer: val }))}
              placeholder="Select player"
            />
          </div>
        )}

        {/* Leaving Player (Substitutions, all sports) */}
        {isSub && (
          <div>
            <label className="block text-sm text-[#FEFCEB] mb-2">Who is coming off?</label>
            <PlayerDropdown
              players={currentTeamPlayers}
              value={eventData.leavingPlayer}
              onChange={(val) => setEventData((prev) => ({ ...prev, leavingPlayer: val }))}
              placeholder="Select player"
            />
          </div>
        )}

        {/* Rugby TRY: optional conversion sub-form. On save we emit a separate
            CONVERSION matchDetail (mobile parity). */}
        {showTryConversionToggle && (
          <div className="space-y-3">
            <label className="flex items-center gap-2 text-sm text-[#FEFCEB] cursor-pointer">
              <input
                type="checkbox"
                checked={!!eventData.tryConverted}
                onChange={(e) =>
                  setEventData((prev) => ({
                    ...prev,
                    tryConverted: e.target.checked,
                    // clear conversion player if toggling off
                    ...(e.target.checked ? {} : { conversionPlayer: null }),
                  }))
                }
                className="accent-[#00E7FF]"
              />
              Did it convert?
            </label>
            {eventData.tryConverted && (
              <div>
                <label className="block text-sm text-[#FEFCEB] mb-2">Who converted?</label>
                <PlayerDropdown
                  players={currentTeamPlayers}
                  value={eventData.conversionPlayer}
                  onChange={(val) => setEventData((prev) => ({ ...prev, conversionPlayer: val }))}
                  placeholder="Select player"
                />
              </div>
            )}
          </div>
        )}

        {/* Form footer Buttons */}
        <div className="flex justify-end gap-3">
          <button
            onClick={handleCancelEvent}
            className="px-6 py-3 text-sm font-medium text-app-text bg-app-card rounded-full hover:text-[#FEFCEB] transition-colors cursor-pointer"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirmEvent}
            className="px-6 py-3 rounded-full bg-[#00E7FF] text-[#081725] text-sm font-medium transition-colors"
          >
            Add
          </button>
        </div>
      </div>
    );
  };

  return (
    <ClubbieModal
      isOpen={isOpen}
      toggle={onClose}
      title={type === "edit" ? "Edit result" : "Add result"}
      width="600px"
      maxHeight="90vh"
    >
      <div className="h-full flex flex-col">
        {/* Match Results Header */}
        <div className="flex flex-col flex-1 min-h-0">

          <p className="text-[#FEFCEB] text-base mb-4">Match results</p>

          {/* Teams and Scores */}
          <div className="flex items-center justify-between">
            {/* Home Team */}
            <div className="flex flex-col items-center gap-2 flex-1">
              <TeamBadge src={homeTeam.crest} alt={homeTeam.title} className="object-contain" />
              <h4 className="text-[#FEFCEB] text-sm font-bold text-center">
                {homeTeam.title}
              </h4>
            </div>

            {/* Scores - Editable Inputs */}
            <div className="flex gap-4 sm:gap-12">
              <input
                type="number"
                min="0"
                value={homeScore}
                onChange={(e) => setHomeScore(e.target.value === "" ? "" : parseInt(e.target.value) || 0)}
                className="w-[71px] h-11 bg-[#021529] rounded-full text-center text-[#E5E5D6] text-l font-bold border border-[#021529] focus:outline-none focus:border-[#00BCD2] transition-colors [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
              />
              <input
                type="number"
                min="0"
                value={awayScore}
                onChange={(e) => setAwayScore(e.target.value === "" ? "" : parseInt(e.target.value) || 0)}
                className="w-[71px] h-11 bg-[#021529] rounded-full text-center text-[#E5E5D6] text-l font-bold border border-[#021529] focus:outline-none focus:border-[#00BCD2] transition-colors [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
              />
            </div>

            {/* Away Team */}
            <div className="flex flex-col items-center gap-2 flex-1">
              <TeamBadge src={awayTeam.crest} alt={awayTeam.title} className="object-contain" />
              <h4 className="text-[#FEFCEB] text-sm font-bold text-center">
                {awayTeam.title}
              </h4>
            </div>
          </div>

          {/* Events List */}
          <div className="min-h-0 overflow-y-auto mb-4 mt-3">
            {eventsWithScores.map((event, index) => (
              <div>
                <div
                  key={index}
                  className="group flex justify-between items-center bg-[#001326] border border-app-border h-16 rounded-l-[40px] rounded-r-[20px] pl-0 pr-4"
                >
                  {/* Left Side */}
                  <div className="flex items-center gap-3">
                      <div className="w-16 h-16 bg-[#00213D] rounded-[40px] flex items-center justify-center">
                        {getEventIcon(event.matchDetailType)}
                      </div>
                    <div>
                      <p className="text-[#FEFCEB] font-medium text-sm">
                        {scorePoints[event.matchDetailType] && event.teamName
                          ? `${eventLabels[event.matchDetailType]} - ${event.scoreAtEvent}`
                          : eventLabels[event.matchDetailType] || event.matchDetailType}
                      </p>
                      <p className="text-sm text-app-text-secondary">
                        {event.matchDetailType === MDT.SUBSTITUTIONS ? (
                          <>
                            <SubOffIcon className="inline w-4 h-4" />
                            <span className="text-[#989D99] ml-1">{event.leavingPlayerName}</span>
                            <SubOnIcon className="inline w-4 h-4 ml-2" />
                            <span className="text-[#E5E5D6] ml-1">{event.playerName}</span>
                          </>
                        ) : (
                          <>
                            <span>{event.playerName && `By ${event.playerName}`}</span>
                            {event.assistPlayerName && (
                              <span className="ml-1">(Assist {event.assistPlayerName})</span>
                            )}
                          </>
                        )}
                      </p>
                    </div>
                  </div>

                  {/* Right Side */}
                  <div className="flex items-center gap-2">
                    <p className="hidden sm:block text-[#FEFCEB] font-medium text-[12px]">{event.teamName}</p>
                    <TeamBadge
                      containerSize="w-[30px] h-[30px]"
                      size="w-2 h-2"
                      src={event.teamName === homeTeam.title ? homeTeam.crest : awayTeam.crest}
                      alt={event.teamName}
                      className="object-contain"
                    />
                    <button
                      type="button"
                      onClick={() => { userMadeChangesRef.current = true; setEvents((prev) => prev.filter((_, i) => i !== index)); }}
                      className="w-7 h-7 flex sm:hidden sm:group-hover:flex items-center justify-center rounded-full bg-app-error hover:bg-[#FF7B7B]/20 transition-colors ml-1"
                    >
                      <X size={14} className="text-bg-app-bg" />
                    </button>
                  </div>
                </div>

                {index != events?.length - 1 && <div className="flex justify-center w-16">
                  <div className="w-0.5 h-3 bg-app-border">
                  </div>
                </div>}
              </div>
            ))}
          </div>

          {/* Add Event Button */}
          <div className="mb-5">
            <button
              ref={addEventRef}
              onClick={() => {
                if (openEventMenu) { setOpenEventMenu(false); return; }
                const rect = addEventRef.current.getBoundingClientRect();
                const dropdownHeight = 5 * 49; // 5 items × ~49px each
                const spaceBelow = window.innerHeight - rect.bottom - 8;
                const dropUp = spaceBelow < dropdownHeight && rect.top > dropdownHeight;
                setEventMenuPos({
                  top: rect.bottom + 4,
                  bottom: window.innerHeight - rect.top + 4,
                  left: Math.min(rect.left, window.innerWidth - rect.width - 8),
                  width: Math.min(rect.width, window.innerWidth - 16),
                  dropUp,
                });
                setOpenEventMenu(true);
              }}
              className="w-full h-11 bg-[#082441] text-[#FEFCEB] rounded-full hover:bg-[#0a2d52] transition-colors flex items-center justify-center gap-2"
            >
              <AddIcon className="w-5 h-5" />
              Add an event
            </button>

            {/* Event Type Dropdown — portal so it's never clipped by modal overflow */}
            {openEventMenu && createPortal(
              <div
                ref={dropdownRef}
                onMouseDown={(e) => e.stopPropagation()}
                style={{
                  position: "fixed",
                  top: eventMenuPos.dropUp ? undefined : eventMenuPos.top,
                  bottom: eventMenuPos.dropUp ? eventMenuPos.bottom : undefined,
                  left: eventMenuPos.left,
                  width: eventMenuPos.width,
                  zIndex: 999999,
                }}
                className="bg-app-bg border border-[#192634] rounded-[20px] shadow-lg overflow-hidden"
              >
                {pickerTypes.map((type) => (
                  <button
                    key={type}
                    onClick={() => handleAddEvent(type)}
                    className="w-full px-3 py-3 flex items-center gap-3 text-[#FEFCEB] text-sm font-medium hover:bg-[#192634] transition-colors border-b border-[#192634] last:border-b-0"
                  >
                    {getEventIcon(type) || <AddIcon className="w-5 h-5" />}
                    {eventLabels[type] || type}
                  </button>
                ))}
              </div>,
              document.body
            )}
          </div>

          {/* Event Form */}
          {renderEventForm()}

        </div>

        {/* Footer Buttons */}
        {!activeEventType && (
          <div className="flex justify-end gap-3">
            <button
              onClick={onClose}
              className="px-6 py-3 text-sm font-medium text-app-text bg-app-card rounded-full hover:text-[#FEFCEB] transition-colors cursor-pointer"
            >
              Cancel
            </button>
            <button
              onClick={handleSaveResults}
              className="px-6 py-3 rounded-full bg-[#00E7FF] text-[#081725] text-sm font-medium transition-colors"
            >
              Save result
            </button>
          </div>
        )}
      </div>
    </ClubbieModal>
  );
};

const PlayerDropdown = ({ players, value, onChange, placeholder }) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [pos, setPos] = useState({ top: 0, bottom: 0, left: 0, width: 0, dropUp: false });
  const triggerRef = useRef(null);
  const dropdownRef = useRef(null);
  const searchRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (
        triggerRef.current && !triggerRef.current.contains(e.target) &&
        dropdownRef.current && !dropdownRef.current.contains(e.target)
      ) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => {
    if (open) { setSearch(""); setTimeout(() => searchRef.current?.focus(), 50); }
  }, [open]);

  const handleOpen = () => {
    if (open) { setOpen(false); return; }
    const rect = triggerRef.current.getBoundingClientRect();
    // search row ~40px + skip row ~49px + up to 5 players ~245px
    const dropdownHeight = 334;
    const spaceBelow = window.innerHeight - rect.bottom - 8;
    const dropUp = spaceBelow < dropdownHeight && rect.top > dropdownHeight;
    setPos({
      top: rect.bottom + 4,
      bottom: window.innerHeight - rect.top + 4,
      left: Math.min(rect.left, window.innerWidth - rect.width - 8),
      width: Math.min(rect.width, window.innerWidth - 16),
      dropUp,
    });
    setOpen(true);
  };

  const filtered = players.filter((p) =>
    p.label.toLowerCase().includes(search.toLowerCase())
  );

  const isSkipped = value === "";
  const selected = value && !isSkipped ? players.find((p) => p.value === value) : null;

  return (
    <div ref={triggerRef}>
      <button
        type="button"
        onClick={handleOpen}
        className="w-full bg-app-surface border border-app-surface rounded-3xl px-4 py-2.5 text-sm text-left transition-colors outline-none focus:border-blue-shade-2 flex items-center justify-between"
      >
        <span className={selected || isSkipped ? "text-[#FEFCEB]" : "text-app-text-secondary"}>
          {isSkipped ? "Skip" : selected ? selected.label : placeholder}
        </span>
        <ChevronDown
          size={16}
          className={`shrink-0 text-app-text-secondary transition-transform duration-200 ${open ? "rotate-180" : ""}`}
        />
      </button>
      {open && createPortal(
        <div
          ref={dropdownRef}
          onMouseDown={(e) => e.stopPropagation()}
          style={{
            position: "fixed",
            top: pos.dropUp ? undefined : pos.top,
            bottom: pos.dropUp ? pos.bottom : undefined,
            left: pos.left,
            width: pos.width,
            zIndex: 999999,
          }}
          className="bg-app-bg border border-[#192634] rounded-[20px] shadow-lg overflow-hidden"
        >
          {/* Search */}
          <div className="px-3 py-2 border-b border-[#192634] flex items-center gap-2">
            <Search size={14} className="shrink-0 text-app-text-secondary" />
            <input
              ref={searchRef}
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search"
              className="flex-1 bg-transparent text-sm text-[#FEFCEB] placeholder-app-text-secondary outline-none"
            />
          </div>
          {/* Skip */}
          <button
            type="button"
            onClick={() => { onChange(""); setOpen(false); }}
            className="w-full px-3 py-3 flex items-center gap-2 text-sm font-medium text-app-text hover:bg-[#192634] transition-colors border-b border-[#192634]"
          >
            <div className="w-4 h-4 rounded-full border border-app-text flex items-center justify-center shrink-0">
              <div className="w-1.5 h-0.5 bg-app-text" />
            </div>
            Skip
          </button>
          {/* Player list */}
          <div className="max-h-48 overflow-y-auto">
            {filtered.length === 0 ? (
              <div className="px-3 py-3 text-sm text-app-text-secondary">No players found</div>
            ) : (
              filtered.map((player) => (
                <button
                  key={player.value}
                  type="button"
                  onClick={() => { onChange(player.value); setOpen(false); }}
                  className={`w-full px-3 py-3 flex items-center text-sm font-medium transition-colors border-b border-[#192634] last:border-b-0 ${
                    value === player.value ? "text-app-accent" : "text-[#FEFCEB] hover:bg-[#192634]"
                  }`}
                >
                  {player.label}
                </button>
              ))
            )}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default AddResultModal;
